import type { FxNode, FxType } from "../types";
import { deepClone } from "./deepClone";
import { equalPowerPanGains } from "./slotMix";

/** Default params when a node omits keys. */
export const FX_DEFAULT_PARAMS: Record<FxType, Record<string, number>> = {
  distortion: { drive: 8 },
  bitcrush: { bits: 8, downsample: 4 },
  delay: { timeMs: 250, feedback: 0.35 },
  chorus: { rateHz: 1.2, depthMs: 3.5, delayMs: 8 },
  reverb: { room: 0.55, damp: 0.4 },
  colorReverb: {
    preGain: 1,
    gain: 1,
    feedback: 0.7,
    damping: 0.55,
    ducking: 0.7,
    width: 0.3,
    predelayMix: 0.1,
    saturateWet: 0.1,
    satDrive: 3.5,
  },
  compressor: {
    thresholdDb: -18,
    ratio: 4,
    attackMs: 12,
    releaseMs: 120,
    kneeDb: 6,
    makeupDb: 2,
    detectorHpfHz: 80,
  },
  /** 5-band master EQ: low shelf · 3 peaks · high shelf (gains in dB). */
  eq: {
    lowDb: 0,
    lowMidDb: 0,
    midDb: 0,
    highMidDb: 0,
    highDb: 0,
  },
  utility: {
    gainDb: 0,
    pan: 0,
    widthPct: 100,
    mono: 0,
    invertL: 0,
    invertR: 0,
  },
  /**
   * Insert filter. `mode`: 0 LPF · 1 HPF · 2 BPF · 3 notch · 4 Moog ladder.
   * Resonance 0..1; drive soft-saturates the input before the filter.
   */
  filter: {
    mode: 0,
    cutoffHz: 1200,
    resonance: 0.35,
    drive: 1,
  },
  /** Peak limiter (wet=1 replace). Ceiling is the max absolute output. */
  limiter: {
    ceilingDb: -0.3,
    releaseMs: 80,
    makeupDb: 0,
  },
  flanger: {
    rateHz: 0.35,
    depthMs: 1.2,
    delayMs: 2.5,
    feedback: 0.55,
  },
  phaser: {
    rateHz: 0.4,
    depth: 0.7,
    feedback: 0.45,
    stages: 6,
    centerHz: 900,
  },
  /**
   * Tape-ish saturator. `mix` is internal dry/wet (node wet defaults to 1).
   * `tone` 0 = dark / dull, 1 = bright / open.
   */
  saturation: {
    drive: 4,
    tone: 0.55,
    mix: 0.7,
  },
};

type DistortionState = { type: "distortion" };
type BitcrushState = {
  type: "bitcrush";
  hold: number;
  counter: number;
};
type DelayState = {
  type: "delay";
  buffer: Float32Array;
  write: number;
};
type ChorusState = {
  type: "chorus";
  buffer: Float32Array;
  write: number;
  phase: number;
};
type CombState = {
  buffer: Float32Array;
  index: number;
  filterStore: number;
};
type AllpassState = {
  buffer: Float32Array;
  index: number;
};
type ReverbState = {
  type: "reverb";
  combs: CombState[];
  allpasses: AllpassState[];
};

/** Pico-inspired compact room: fixed-length delays + Hadamard feedback. */
type ColorReverbState = {
  type: "colorReverb";
  predelay: Float32Array;
  pdPos: number;
  d1: Float32Array;
  d2: Float32Array;
  d3: Float32Array;
  d4: Float32Array;
  p1: number;
  p2: number;
  p3: number;
  p4: number;
  lp1: number;
  lp2: number;
  lp3: number;
  lp4: number;
};

/** Feed-forward soft-knee compressor with optional detector HPF. */
type CompressorState = {
  type: "compressor";
  env: number;
  hpfY: number;
  hpfX1: number;
};

type BiquadState = {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
};

/** 5-band EQ: low shelf, low-mid / mid / high-mid peaks, high shelf. */
type EqState = {
  type: "eq";
  bands: BiquadState[];
  /** Last gains used to build coeffs (detect changes). */
  lastGains: number[];
  lastSampleRate: number;
};

type UtilityState = { type: "utility" };

/** SVF / ladder insert filter. */
type FilterState = {
  type: "filter";
  /** SVF integrators */
  z1: number;
  z2: number;
  /** Moog ladder poles */
  l1: number;
  l2: number;
  l3: number;
  l4: number;
};

/** Peak limiter envelope follower. */
type LimiterState = {
  type: "limiter";
  env: number;
};

/** Flanger = short modulated delay with feedback. */
type FlangerState = {
  type: "flanger";
  buffer: Float32Array;
  write: number;
  phase: number;
};

/** Multi-stage allpass phaser. */
type PhaserState = {
  type: "phaser";
  phase: number;
  /** Per-stage one-pole allpass state (y[n-1]). */
  ap: number[];
  lastFeedback: number;
};

/** Tape-ish saturator: pre-emphasis → soft clip → tone LP. */
type SaturationState = {
  type: "saturation";
  lp: number;
  hp: number;
};

/** Linked dual-mono: identical params, independent L/R delay/filter state. */
type MonoFxRuntime =
  | DistortionState
  | BitcrushState
  | DelayState
  | ChorusState
  | ReverbState
  | CompressorState
  | EqState
  | FilterState
  | LimiterState
  | FlangerState
  | PhaserState
  | SaturationState;

type DualMonoState = {
  type: "dualMono";
  left: MonoFxRuntime;
  right: MonoFxRuntime;
};

export type FxRuntimeNode = DualMonoState | ColorReverbState | UtilityState;

export type FxChainState = {
  nodes: FxNode[];
  runtime: FxRuntimeNode[];
  /**
   * Smoothed effective wet per node (targets `enabled ? wet : 0`).
   * Prevents clicks when scrubbing wet or toggling enable.
   */
  wetSmooth: number[];
};

export type StereoSample = { l: number; r: number };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function param(node: FxNode, key: string, fallback: number): number {
  const value = node.params[key];
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function createDelayBuffer(sampleRate: number, maxMs: number): Float32Array {
  const frames = Math.max(1, Math.ceil((sampleRate * maxMs) / 1000));
  return new Float32Array(frames);
}

function createComb(sampleRate: number, delayMs: number): CombState {
  return {
    buffer: createDelayBuffer(sampleRate, delayMs),
    index: 0,
    filterStore: 0,
  };
}

function createAllpass(sampleRate: number, delayMs: number): AllpassState {
  return {
    buffer: createDelayBuffer(sampleRate, delayMs),
    index: 0,
  };
}

/** Predelay / network lengths from the shady coloration design. */
const COLOR_PD_LEN = 223;
const COLOR_D1_LEN = 443;
const COLOR_D2_LEN = 661;
const COLOR_D3_LEN = 997;
const COLOR_D4_LEN = 1499;

function createMonoRuntime(node: FxNode, sampleRate: number): MonoFxRuntime {
  switch (node.type) {
    case "distortion":
      return { type: "distortion" };
    case "bitcrush":
      return { type: "bitcrush", hold: 0, counter: 0 };
    case "delay":
      return {
        type: "delay",
        buffer: createDelayBuffer(sampleRate, 2000),
        write: 0,
      };
    case "chorus":
      return {
        type: "chorus",
        buffer: createDelayBuffer(sampleRate, 50),
        write: 0,
        phase: 0,
      };
    case "reverb":
      return {
        type: "reverb",
        combs: [
          createComb(sampleRate, 29.7),
          createComb(sampleRate, 37.1),
          createComb(sampleRate, 41.1),
          createComb(sampleRate, 43.7),
        ],
        allpasses: [
          createAllpass(sampleRate, 5.0),
          createAllpass(sampleRate, 1.7),
        ],
      };
    case "compressor":
      return { type: "compressor", env: 0, hpfY: 0, hpfX1: 0 };
    case "eq":
      return {
        type: "eq",
        bands: Array.from({ length: 5 }, () => createBiquadUnity()),
        lastGains: [NaN, NaN, NaN, NaN, NaN],
        lastSampleRate: 0,
      };
    case "filter":
      return { type: "filter", z1: 0, z2: 0, l1: 0, l2: 0, l3: 0, l4: 0 };
    case "limiter":
      return { type: "limiter", env: 0 };
    case "flanger":
      return {
        type: "flanger",
        buffer: createDelayBuffer(sampleRate, 40),
        write: 0,
        phase: 0,
      };
    case "phaser":
      return {
        type: "phaser",
        phase: 0,
        ap: Array.from({ length: 12 }, () => 0),
        lastFeedback: 0,
      };
    case "saturation":
      return { type: "saturation", lp: 0, hp: 0 };
    default:
      // colorReverb / utility are stereo-native; fall back to transparent distortion.
      return { type: "distortion" };
  }
}

function createColorReverbRuntime(): ColorReverbState {
  return {
    type: "colorReverb",
    predelay: new Float32Array(COLOR_PD_LEN),
    pdPos: 0,
    d1: new Float32Array(COLOR_D1_LEN),
    d2: new Float32Array(COLOR_D2_LEN),
    d3: new Float32Array(COLOR_D3_LEN),
    d4: new Float32Array(COLOR_D4_LEN),
    p1: 0,
    p2: 0,
    p3: 0,
    p4: 0,
    lp1: 0,
    lp2: 0,
    lp3: 0,
    lp4: 0,
  };
}

function createRuntime(node: FxNode, sampleRate: number): FxRuntimeNode {
  if (node.type === "colorReverb") {
    return createColorReverbRuntime();
  }
  if (node.type === "utility") {
    return { type: "utility" };
  }
  return {
    type: "dualMono",
    left: createMonoRuntime(node, sampleRate),
    right: createMonoRuntime(node, sampleRate),
  };
}

export function createFxChainState(effects: FxNode[], sampleRate: number): FxChainState {
  const nodes = effects.map(fx => deepClone(fx));
  return {
    nodes,
    runtime: nodes.map(node => createRuntime(node, sampleRate)),
    wetSmooth: nodes.map(node => (node.enabled ? clamp(node.wet, 0, 1) : 0)),
  };
}

export function clearFxChainState(): FxChainState {
  return { nodes: [], runtime: [], wetSmooth: [] };
}

/**
 * Live-update an FX stack without wiping delay/reverb/filter memory.
 * Recreates runtime only when a node's type changes or a slot is new.
 */
export function syncFxChainState(
  state: FxChainState,
  effects: FxNode[],
  sampleRate: number,
): void {
  const nextNodes = effects.map(fx => deepClone(fx));
  const nextRuntime: FxRuntimeNode[] = [];
  const nextWet: number[] = [];
  for (let i = 0; i < nextNodes.length; i++) {
    const node = nextNodes[i]!;
    const prev = state.nodes[i];
    const prevRt = state.runtime[i];
    if (prev && prevRt && prev.type === node.type) {
      nextRuntime[i] = prevRt;
      nextWet[i] = state.wetSmooth[i] ?? (node.enabled ? clamp(node.wet, 0, 1) : 0);
    } else {
      nextRuntime[i] = createRuntime(node, sampleRate);
      nextWet[i] = node.enabled ? clamp(node.wet, 0, 1) : 0;
    }
  }
  state.nodes = nextNodes;
  state.runtime = nextRuntime;
  state.wetSmooth = nextWet;
}

/** Patch one node; rebuilds that slot's runtime when type changes. */
export function patchFxChainState(
  state: FxChainState,
  index: number,
  patch: Partial<FxNode>,
  sampleRate: number,
): void {
  const existing = state.nodes[index];
  if (!existing) {
    return;
  }
  const next: FxNode = {
    ...existing,
    ...patch,
    params: { ...existing.params, ...patch.params },
  };
  state.nodes[index] = next;
  if (patch.type && patch.type !== existing.type) {
    state.runtime[index] = createRuntime(next, sampleRate);
    state.wetSmooth[index] = next.enabled ? clamp(next.wet, 0, 1) : 0;
  } else if (!state.runtime[index]) {
    state.runtime[index] = createRuntime(next, sampleRate);
    state.wetSmooth[index] = next.enabled ? clamp(next.wet, 0, 1) : 0;
  }
}

function processDistortion(input: number, node: FxNode): number {
  const drive = Math.max(1, param(node, "drive", FX_DEFAULT_PARAMS.distortion.drive!));
  return Math.tanh(input * drive);
}

function processBitcrush(input: number, node: FxNode, runtime: BitcrushState): number {
  const bits = clamp(param(node, "bits", FX_DEFAULT_PARAMS.bitcrush.bits!), 1, 16);
  const downsample = Math.max(1, Math.floor(param(node, "downsample", FX_DEFAULT_PARAMS.bitcrush.downsample!)));
  runtime.counter += 1;
  if (runtime.counter >= downsample) {
    runtime.counter = 0;
    const levels = 2 ** bits;
    runtime.hold = Math.round(input * (levels / 2)) / (levels / 2);
  }
  return runtime.hold;
}

function readDelay(buffer: Float32Array, write: number, delaySamples: number): number {
  const len = buffer.length;
  const read = (write - delaySamples + len * 4) % len;
  const i0 = Math.floor(read);
  const i1 = (i0 + 1) % len;
  const frac = read - i0;
  return buffer[i0]! * (1 - frac) + buffer[i1]! * frac;
}

function processDelay(input: number, node: FxNode, runtime: DelayState, sampleRate: number): number {
  const timeMs = clamp(param(node, "timeMs", FX_DEFAULT_PARAMS.delay.timeMs!), 1, 2000);
  const feedback = clamp(param(node, "feedback", FX_DEFAULT_PARAMS.delay.feedback!), 0, 0.95);
  const delaySamples = Math.min(runtime.buffer.length - 1, (timeMs / 1000) * sampleRate);
  const delayed = finiteOrZero(readDelay(runtime.buffer, runtime.write, delaySamples));
  const written = finiteOrZero(input) + delayed * feedback;
  runtime.buffer[runtime.write] = Number.isFinite(written) ? written : 0;
  runtime.write = (runtime.write + 1) % runtime.buffer.length;
  return delayed;
}

function processChorus(input: number, node: FxNode, runtime: ChorusState, sampleRate: number): number {
  const rateHz = Math.max(0.01, param(node, "rateHz", FX_DEFAULT_PARAMS.chorus.rateHz!));
  const depthMs = Math.max(0, param(node, "depthMs", FX_DEFAULT_PARAMS.chorus.depthMs!));
  const baseMs = Math.max(0.5, param(node, "delayMs", FX_DEFAULT_PARAMS.chorus.delayMs!));
  runtime.phase += rateHz / sampleRate;
  if (runtime.phase >= 1) {
    runtime.phase -= 1;
  }
  const mod = Math.sin(2 * Math.PI * runtime.phase);
  const delaySamples = Math.min(
    runtime.buffer.length - 2,
    ((baseMs + mod * depthMs) / 1000) * sampleRate,
  );
  const delayed = finiteOrZero(readDelay(runtime.buffer, runtime.write, delaySamples));
  runtime.buffer[runtime.write] = finiteOrZero(input);
  runtime.write = (runtime.write + 1) % runtime.buffer.length;
  return delayed;
}

function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function processComb(input: number, comb: CombState, feedback: number, damp: number): number {
  const delayed = finiteOrZero(comb.buffer[comb.index]!);
  let store = finiteOrZero(comb.filterStore);
  store = delayed * (1 - damp) + store * damp;
  const written = finiteOrZero(input) + store * feedback;
  if (!Number.isFinite(written) || !Number.isFinite(store)) {
    comb.filterStore = 0;
    comb.buffer[comb.index] = 0;
  } else {
    comb.filterStore = store;
    comb.buffer[comb.index] = written;
  }
  comb.index = (comb.index + 1) % comb.buffer.length;
  return delayed;
}

function processAllpass(input: number, ap: AllpassState, feedback = 0.5): number {
  const bufOut = finiteOrZero(ap.buffer[ap.index]!);
  const safeIn = finiteOrZero(input);
  const out = -safeIn + bufOut;
  const written = safeIn + bufOut * feedback;
  ap.buffer[ap.index] = Number.isFinite(written) ? written : 0;
  ap.index = (ap.index + 1) % ap.buffer.length;
  return Number.isFinite(out) ? out : 0;
}

function processReverb(input: number, node: FxNode, runtime: ReverbState): number {
  const room = clamp(param(node, "room", FX_DEFAULT_PARAMS.reverb.room!), 0, 0.98);
  const damp = clamp(param(node, "damp", FX_DEFAULT_PARAMS.reverb.damp!), 0, 1);
  let sum = 0;
  for (const comb of runtime.combs) {
    sum += processComb(input, comb, room, damp);
  }
  let out = sum / runtime.combs.length;
  for (const ap of runtime.allpasses) {
    out = processAllpass(out, ap);
  }
  return out;
}

function colorSat(x: number, drive: number): number {
  const d = Math.max(1, drive);
  return Math.tanh(x * d) / Math.tanh(d);
}

function colorSoftClip(x: number): number {
  return x - x * x * x * 0.18;
}

function colorDampStep(x: number, prev: number, amt: number): number {
  const a = clamp(amt, 0, 0.999);
  return x * (1 - a) + prev * a;
}

/**
 * Stereo coloration reverb: mid drives the network; stereo tap matrix + width retained.
 */
function processColorReverbStereo(
  inputL: number,
  inputR: number,
  node: FxNode,
  runtime: ColorReverbState,
): StereoSample {
  const defaults = FX_DEFAULT_PARAMS.colorReverb;
  const preGain = clamp(param(node, "preGain", defaults.preGain!), 0, 2);
  const gain = clamp(param(node, "gain", defaults.gain!), 0, 2);
  const feedback = clamp(param(node, "feedback", defaults.feedback!), 0, 0.98);
  const damping = clamp(param(node, "damping", defaults.damping!), 0, 0.99);
  const ducking = clamp(param(node, "ducking", defaults.ducking!), 0, 1);
  const width = clamp(param(node, "width", defaults.width!), 0, 1);
  const predelayMix = clamp(param(node, "predelayMix", defaults.predelayMix!), 0, 1);
  const saturateWet = clamp(param(node, "saturateWet", defaults.saturateWet!), 0, 1);
  const satDrive = clamp(param(node, "satDrive", defaults.satDrive!), 1, 12);

  const dryIn = 0.5 * (finiteOrZero(inputL) + finiteOrZero(inputR)) * preGain;
  const pdOut = finiteOrZero(runtime.predelay[runtime.pdPos]!);
  runtime.predelay[runtime.pdPos] = dryIn;
  runtime.pdPos = (runtime.pdPos + 1) % runtime.predelay.length;

  const excite = dryIn * (1 - predelayMix) + pdOut * predelayMix;

  const y1 = finiteOrZero(runtime.d1[runtime.p1]!);
  const y2 = finiteOrZero(runtime.d2[runtime.p2]!);
  const y3 = finiteOrZero(runtime.d3[runtime.p3]!);
  const y4 = finiteOrZero(runtime.d4[runtime.p4]!);

  runtime.lp1 = colorDampStep(y1, runtime.lp1, damping);
  runtime.lp2 = colorDampStep(y2, runtime.lp2, damping);
  runtime.lp3 = colorDampStep(y3, runtime.lp3, damping);
  runtime.lp4 = colorDampStep(y4, runtime.lp4, damping);

  const h1 = (runtime.lp1 + runtime.lp2 + runtime.lp3 + runtime.lp4) * 0.5;
  const h2 = (runtime.lp1 - runtime.lp2 + runtime.lp3 - runtime.lp4) * 0.5;
  const h3 = (runtime.lp1 + runtime.lp2 - runtime.lp3 - runtime.lp4) * 0.5;
  const h4 = (runtime.lp1 - runtime.lp2 - runtime.lp3 + runtime.lp4) * 0.5;

  const w1 = colorSoftClip(excite + h2 * feedback);
  const w2 = colorSoftClip(excite + h3 * feedback);
  const w3 = colorSoftClip(excite + h4 * feedback);
  const w4 = colorSoftClip(excite + h1 * feedback);

  runtime.d1[runtime.p1] = finiteOrZero(w1);
  runtime.d2[runtime.p2] = finiteOrZero(w2);
  runtime.d3[runtime.p3] = finiteOrZero(w3);
  runtime.d4[runtime.p4] = finiteOrZero(w4);

  runtime.p1 = (runtime.p1 + 1) % runtime.d1.length;
  runtime.p2 = (runtime.p2 + 1) % runtime.d2.length;
  runtime.p3 = (runtime.p3 + 1) % runtime.d3.length;
  runtime.p4 = (runtime.p4 + 1) % runtime.d4.length;

  let wetL = y1 * 0.62 + y2 * 0.18 + y3 * 0.31 - y4 * 0.12;
  let wetR = y1 * -0.1 + y2 * 0.58 - y3 * 0.14 + y4 * 0.36;
  const mid = 0.5 * (wetL + wetR);
  let side = 0.5 * (wetL - wetR);
  side *= width * 1.8;
  wetL = mid + side;
  wetR = mid - side;

  const dryEnv = clamp(Math.abs(dryIn), 0, 1);
  let duck = 1 - dryEnv * ducking;
  duck = clamp(duck, 0.12, 1);
  wetL *= duck;
  wetR *= duck;

  const satL = colorSat(wetL, satDrive);
  const satR = colorSat(wetR, satDrive);
  wetL = wetL * (1 - saturateWet) + satL * saturateWet;
  wetR = wetR * (1 - saturateWet) + satR * saturateWet;
  wetL *= gain;
  wetR *= gain;

  return {
    l: clamp(finiteOrZero(wetL), -0.95, 0.95),
    r: clamp(finiteOrZero(wetR), -0.95, 0.95),
  };
}

/** Soft-knee gain reduction in dB (≤ 0). */
function compressorGainReductionDb(
  levelDb: number,
  thresholdDb: number,
  ratio: number,
  kneeDb: number,
): number {
  const slope = 1 / Math.max(1, ratio) - 1;
  const halfKnee = Math.max(0, kneeDb) * 0.5;
  const overshoot = levelDb - thresholdDb;
  if (overshoot <= -halfKnee) {
    return 0;
  }
  if (halfKnee > 0 && overshoot < halfKnee) {
    const t = overshoot + halfKnee;
    return slope * (t * t) / (2 * kneeDb);
  }
  return overshoot * slope;
}

/**
 * Feed-forward peak compressor: detector HPF → envelope → soft knee → makeup.
 * Use node wet < 1 for parallel compression.
 */
function processCompressor(
  input: number,
  node: FxNode,
  runtime: CompressorState,
  sampleRate: number,
): number {
  const defaults = FX_DEFAULT_PARAMS.compressor;
  const thresholdDb = clamp(param(node, "thresholdDb", defaults.thresholdDb!), -60, 0);
  const ratio = clamp(param(node, "ratio", defaults.ratio!), 1, 20);
  const attackMs = clamp(param(node, "attackMs", defaults.attackMs!), 0.1, 200);
  const releaseMs = clamp(param(node, "releaseMs", defaults.releaseMs!), 1, 2000);
  const kneeDb = clamp(param(node, "kneeDb", defaults.kneeDb!), 0, 24);
  const makeupDb = clamp(param(node, "makeupDb", defaults.makeupDb!), -24, 24);
  const detectorHpfHz = clamp(param(node, "detectorHpfHz", defaults.detectorHpfHz!), 0, 400);

  const x = finiteOrZero(input);
  let det = x;
  if (detectorHpfHz > 0.5) {
    const rc = 1 / (2 * Math.PI * detectorHpfHz);
    const dt = 1 / Math.max(1, sampleRate);
    const a = rc / (rc + dt);
    runtime.hpfY = a * (runtime.hpfY + x - runtime.hpfX1);
    runtime.hpfX1 = x;
    det = runtime.hpfY;
  }

  const level = Math.abs(det);
  const attackCoeff = 1 - Math.exp(-1 / (Math.max(0.0001, attackMs / 1000) * sampleRate));
  const releaseCoeff = 1 - Math.exp(-1 / (Math.max(0.001, releaseMs / 1000) * sampleRate));
  if (level > runtime.env) {
    runtime.env += (level - runtime.env) * attackCoeff;
  } else {
    runtime.env += (level - runtime.env) * releaseCoeff;
  }
  if (!Number.isFinite(runtime.env) || runtime.env < 0) {
    runtime.env = 0;
  }

  const levelDb = 20 * Math.log10(runtime.env + 1e-8);
  const grDb = compressorGainReductionDb(levelDb, thresholdDb, ratio, kneeDb);
  const gainLin = Math.pow(10, (grDb + makeupDb) / 20);
  return finiteOrZero(x * gainLin);
}

function createBiquadUnity(): BiquadState {
  return {
    b0: 1,
    b1: 0,
    b2: 0,
    a1: 0,
    a2: 0,
    x1: 0,
    x2: 0,
    y1: 0,
    y2: 0,
  };
}

function processBiquad(input: number, b: BiquadState): number {
  const x = finiteOrZero(input);
  const y = b.b0 * x + b.b1 * b.x1 + b.b2 * b.x2 - b.a1 * b.y1 - b.a2 * b.y2;
  b.x2 = b.x1;
  b.x1 = x;
  b.y2 = b.y1;
  b.y1 = finiteOrZero(y);
  return b.y1;
}

/** RBJ peaking EQ (a0-normalized). */
function setPeaking(b: BiquadState, sampleRate: number, freq: number, q: number, gainDb: number): void {
  if (Math.abs(gainDb) < 0.01) {
    b.b0 = 1;
    b.b1 = 0;
    b.b2 = 0;
    b.a1 = 0;
    b.a2 = 0;
    return;
  }
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * clamp(freq, 20, sampleRate * 0.45)) / sampleRate;
  const cos = Math.cos(w0);
  const sin = Math.sin(w0);
  const alpha = sin / (2 * Math.max(0.1, q));
  const b0 = 1 + alpha * A;
  const b1 = -2 * cos;
  const b2 = 1 - alpha * A;
  const a0 = 1 + alpha / A;
  const a1 = -2 * cos;
  const a2 = 1 - alpha / A;
  b.b0 = b0 / a0;
  b.b1 = b1 / a0;
  b.b2 = b2 / a0;
  b.a1 = a1 / a0;
  b.a2 = a2 / a0;
}

/** RBJ low / high shelf (S = 1). */
function setShelf(
  b: BiquadState,
  sampleRate: number,
  freq: number,
  gainDb: number,
  kind: "low" | "high",
): void {
  if (Math.abs(gainDb) < 0.01) {
    b.b0 = 1;
    b.b1 = 0;
    b.b2 = 0;
    b.a1 = 0;
    b.a2 = 0;
    return;
  }
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * clamp(freq, 20, sampleRate * 0.45)) / sampleRate;
  const cos = Math.cos(w0);
  const sin = Math.sin(w0);
  const alpha = (sin / 2) * Math.SQRT2;
  const twoSqrtAAlpha = 2 * Math.sqrt(A) * alpha;
  let b0: number;
  let b1: number;
  let b2: number;
  let a0: number;
  let a1: number;
  let a2: number;
  if (kind === "low") {
    b0 = A * ((A + 1) - (A - 1) * cos + twoSqrtAAlpha);
    b1 = 2 * A * ((A - 1) - (A + 1) * cos);
    b2 = A * ((A + 1) - (A - 1) * cos - twoSqrtAAlpha);
    a0 = (A + 1) + (A - 1) * cos + twoSqrtAAlpha;
    a1 = -2 * ((A - 1) + (A + 1) * cos);
    a2 = (A + 1) + (A - 1) * cos - twoSqrtAAlpha;
  } else {
    b0 = A * ((A + 1) + (A - 1) * cos + twoSqrtAAlpha);
    b1 = -2 * A * ((A - 1) + (A + 1) * cos);
    b2 = A * ((A + 1) + (A - 1) * cos - twoSqrtAAlpha);
    a0 = (A + 1) - (A - 1) * cos + twoSqrtAAlpha;
    a1 = 2 * ((A - 1) - (A + 1) * cos);
    a2 = (A + 1) - (A - 1) * cos - twoSqrtAAlpha;
  }
  b.b0 = b0 / a0;
  b.b1 = b1 / a0;
  b.b2 = b2 / a0;
  b.a1 = a1 / a0;
  b.a2 = a2 / a0;
}

const EQ_BANDS = [
  { key: "lowDb", kind: "lowShelf" as const, freq: 80, q: 0.7 },
  { key: "lowMidDb", kind: "peak" as const, freq: 250, q: 0.9 },
  { key: "midDb", kind: "peak" as const, freq: 1000, q: 0.9 },
  { key: "highMidDb", kind: "peak" as const, freq: 4000, q: 0.9 },
  { key: "highDb", kind: "highShelf" as const, freq: 10000, q: 0.7 },
] as const;

function syncEqCoeffs(node: FxNode, runtime: EqState, sampleRate: number): void {
  const defaults = FX_DEFAULT_PARAMS.eq;
  const gains = EQ_BANDS.map(band =>
    clamp(param(node, band.key, defaults[band.key]!), -18, 18),
  );
  const needsUpdate =
    runtime.lastSampleRate !== sampleRate
    || gains.some((g, i) => g !== runtime.lastGains[i]);
  if (!needsUpdate) {
    return;
  }
  runtime.lastSampleRate = sampleRate;
  runtime.lastGains = gains;
  for (let i = 0; i < EQ_BANDS.length; i++) {
    const band = EQ_BANDS[i]!;
    const bq = runtime.bands[i]!;
    const gainDb = gains[i]!;
    if (band.kind === "lowShelf") {
      setShelf(bq, sampleRate, band.freq, gainDb, "low");
    } else if (band.kind === "highShelf") {
      setShelf(bq, sampleRate, band.freq, gainDb, "high");
    } else {
      setPeaking(bq, sampleRate, band.freq, band.q, gainDb);
    }
  }
}

function processEq(input: number, node: FxNode, runtime: EqState, sampleRate: number): number {
  syncEqCoeffs(node, runtime, sampleRate);
  let sample = finiteOrZero(input);
  for (const band of runtime.bands) {
    sample = processBiquad(sample, band);
  }
  return finiteOrZero(sample);
}

/**
 * Utility: phase → mono → width → pan → gain.
 * Pan uses equal-power with √2 compensation so center is unity gain.
 */
function processUtility(inputL: number, inputR: number, node: FxNode): StereoSample {
  const defaults = FX_DEFAULT_PARAMS.utility;
  let l = finiteOrZero(inputL);
  let r = finiteOrZero(inputR);

  if (param(node, "invertL", defaults.invertL!) >= 0.5) {
    l = -l;
  }
  if (param(node, "invertR", defaults.invertR!) >= 0.5) {
    r = -r;
  }

  if (param(node, "mono", defaults.mono!) >= 0.5) {
    const mid = 0.5 * (l + r);
    l = mid;
    r = mid;
  }

  const widthPct = clamp(param(node, "widthPct", defaults.widthPct!), 0, 200);
  {
    const mid = 0.5 * (l + r);
    let side = 0.5 * (l - r);
    side *= widthPct / 100;
    l = mid + side;
    r = mid - side;
  }

  const pan = clamp(param(node, "pan", defaults.pan!), -100, 100) / 100;
  {
    const { left: gL, right: gR } = equalPowerPanGains(pan);
    const scale = Math.SQRT2;
    l *= gL * scale;
    r *= gR * scale;
  }

  const gainDb = clamp(param(node, "gainDb", defaults.gainDb!), -24, 24);
  const gainLin = Math.pow(10, gainDb / 20);
  l *= gainLin;
  r *= gainLin;

  return { l: finiteOrZero(l), r: finiteOrZero(r) };
}

/**
 * Insert filter: SVF (LPF/HPF/BPF/notch) or 4-pole Moog-style ladder.
 * `mode` 0..4 — see FX_DEFAULT_PARAMS.filter.
 */
function processFilter(
  input: number,
  node: FxNode,
  runtime: FilterState,
  sampleRate: number,
): number {
  const defaults = FX_DEFAULT_PARAMS.filter;
  const mode = Math.round(clamp(param(node, "mode", defaults.mode!), 0, 4));
  const cutoffHz = clamp(param(node, "cutoffHz", defaults.cutoffHz!), 40, 18000);
  const resonance = clamp(param(node, "resonance", defaults.resonance!), 0, 1);
  const drive = Math.max(1, param(node, "drive", defaults.drive!));
  const x = Math.tanh(finiteOrZero(input) * drive);

  if (mode === 4) {
    // Moog-style 4-pole ladder (simplified VA one-pole cascade + feedback).
    const g = Math.tan(Math.PI * Math.min(0.49, Math.max(0.0001, cutoffHz / sampleRate)));
    const g1 = g / (1 + g);
    const k = resonance * 3.8;
    let u = x - k * runtime.l4;
    u = Math.tanh(u);
    runtime.l1 += g1 * (u - runtime.l1);
    runtime.l2 += g1 * (runtime.l1 - runtime.l2);
    runtime.l3 += g1 * (runtime.l2 - runtime.l3);
    runtime.l4 += g1 * (runtime.l3 - runtime.l4);
    runtime.l1 = finiteOrZero(runtime.l1);
    runtime.l2 = finiteOrZero(runtime.l2);
    runtime.l3 = finiteOrZero(runtime.l3);
    runtime.l4 = finiteOrZero(runtime.l4);
    return runtime.l4;
  }

  // State-variable filter (Chamberlin-ish).
  const f = 2 * Math.sin(Math.PI * Math.min(0.49, Math.max(0.0001, cutoffHz / sampleRate)));
  const q = 1 - Math.min(0.95, resonance);
  const low = runtime.z2 + f * runtime.z1;
  const high = x - low - q * runtime.z1;
  const band = runtime.z1 + f * high;
  runtime.z1 = Number.isFinite(band) ? band : 0;
  runtime.z2 = Number.isFinite(low) ? low : 0;
  if (mode === 1) {
    return high;
  }
  if (mode === 2) {
    return band;
  }
  if (mode === 3) {
    return low + high; // notch / cutout
  }
  return low;
}

/**
 * Fast peak limiter: envelope of |x|, gain = min(1, ceiling/env).
 */
function processLimiter(
  input: number,
  node: FxNode,
  runtime: LimiterState,
  sampleRate: number,
): number {
  const defaults = FX_DEFAULT_PARAMS.limiter;
  const ceilingDb = clamp(param(node, "ceilingDb", defaults.ceilingDb!), -24, 0);
  const releaseMs = clamp(param(node, "releaseMs", defaults.releaseMs!), 1, 2000);
  const makeupDb = clamp(param(node, "makeupDb", defaults.makeupDb!), -12, 24);
  const ceiling = Math.pow(10, ceilingDb / 20);
  const x = finiteOrZero(input);
  const level = Math.abs(x);
  // Instant attack (brickwall-ish), smoothed release.
  const releaseCoeff = 1 - Math.exp(-1 / (Math.max(0.001, releaseMs / 1000) * sampleRate));
  if (level > runtime.env) {
    runtime.env = level;
  } else {
    runtime.env += (level - runtime.env) * releaseCoeff;
  }
  if (!Number.isFinite(runtime.env) || runtime.env < 0) {
    runtime.env = 0;
  }
  const gain = runtime.env > 1e-8 ? Math.min(1, ceiling / runtime.env) : 1;
  const makeup = Math.pow(10, makeupDb / 20);
  return finiteOrZero(x * gain * makeup);
}

function processFlanger(
  input: number,
  node: FxNode,
  runtime: FlangerState,
  sampleRate: number,
): number {
  const defaults = FX_DEFAULT_PARAMS.flanger;
  const rateHz = Math.max(0.01, param(node, "rateHz", defaults.rateHz!));
  const depthMs = Math.max(0, param(node, "depthMs", defaults.depthMs!));
  const baseMs = Math.max(0.2, param(node, "delayMs", defaults.delayMs!));
  const feedback = clamp(param(node, "feedback", defaults.feedback!), 0, 0.95);
  runtime.phase += rateHz / sampleRate;
  if (runtime.phase >= 1) {
    runtime.phase -= 1;
  }
  const mod = Math.sin(2 * Math.PI * runtime.phase);
  const delaySamples = Math.min(
    runtime.buffer.length - 2,
    Math.max(1, ((baseMs + mod * depthMs) / 1000) * sampleRate),
  );
  const delayed = finiteOrZero(readDelay(runtime.buffer, runtime.write, delaySamples));
  const x = finiteOrZero(input);
  runtime.buffer[runtime.write] = finiteOrZero(x + delayed * feedback);
  runtime.write = (runtime.write + 1) % runtime.buffer.length;
  // Classic flanger wet: dry + delayed (wet mix is applied by the chain).
  return finiteOrZero(x + delayed);
}

function processPhaser(
  input: number,
  node: FxNode,
  runtime: PhaserState,
  sampleRate: number,
): number {
  const defaults = FX_DEFAULT_PARAMS.phaser;
  const rateHz = Math.max(0.01, param(node, "rateHz", defaults.rateHz!));
  const depth = clamp(param(node, "depth", defaults.depth!), 0, 1);
  const feedback = clamp(param(node, "feedback", defaults.feedback!), 0, 0.95);
  const stages = Math.round(clamp(param(node, "stages", defaults.stages!), 2, 12));
  const centerHz = clamp(param(node, "centerHz", defaults.centerHz!), 100, 8000);

  runtime.phase += rateHz / sampleRate;
  if (runtime.phase >= 1) {
    runtime.phase -= 1;
  }
  const lfo = 0.5 + 0.5 * Math.sin(2 * Math.PI * runtime.phase);
  // Sweep allpass cutoff around center with depth.
  const minHz = Math.max(40, centerHz * (1 - 0.85 * depth));
  const maxHz = Math.min(sampleRate * 0.45, centerHz * (1 + 3 * depth));
  const sweepHz = minHz + (maxHz - minHz) * lfo;
  // One-pole allpass coefficient from cutoff.
  const tan = Math.tan(Math.PI * (sweepHz / sampleRate));
  const a = (1 - tan) / (1 + tan);

  let x = finiteOrZero(input) + runtime.lastFeedback * feedback;
  x = Math.tanh(x); // keep feedback stable
  for (let i = 0; i < stages; i++) {
    const y1 = runtime.ap[i] ?? 0;
    const y = -a * x + y1;
    runtime.ap[i] = finiteOrZero(x + a * y);
    x = finiteOrZero(y);
  }
  runtime.lastFeedback = x;
  // Mix allpassed with dry for classic phaser notches (chain wet still applies).
  return finiteOrZero(0.5 * (finiteOrZero(input) + x));
}

/**
 * Tape-ish saturation: mild HF lift into soft asymmetric clip, then tone lowpass.
 * Even-ish harmonics from asymmetry; tone darkens like oxide/head bump rolled off.
 */
function processSaturation(
  input: number,
  node: FxNode,
  runtime: SaturationState,
  sampleRate: number,
): number {
  const defaults = FX_DEFAULT_PARAMS.saturation;
  const drive = Math.max(1, param(node, "drive", defaults.drive!));
  const tone = clamp(param(node, "tone", defaults.tone!), 0, 1);
  const mix = clamp(param(node, "mix", defaults.mix!), 0, 1);
  const dry = finiteOrZero(input);

  // Soft high-shelf pre-emphasis into the tape (brighter drive = more grit).
  const hpCoeff = Math.exp(-2 * Math.PI * 1200 / Math.max(1, sampleRate));
  runtime.hp = hpCoeff * runtime.hp + (1 - hpCoeff) * dry;
  const pre = dry + (dry - runtime.hp) * (0.15 + 0.35 * tone);

  // Asymmetric soft clip (even harmonics) + gentle tanh glue.
  const driven = pre * drive;
  const shaped = Math.tanh(driven * 0.85)
    + 0.08 * Math.tanh(driven * driven * 0.35) * Math.sign(driven || 1);
  // Compensated makeup so drive≈1 is near unity.
  const wetRaw = shaped * (1.15 / Math.tanh(0.85 * Math.min(drive, 8)));

  // Post tone: one-pole LP — low tone = darker tape, high = more open.
  const lpHz = 800 + tone * tone * 14000;
  const lpCoeff = Math.exp(-2 * Math.PI * lpHz / Math.max(1, sampleRate));
  runtime.lp = lpCoeff * runtime.lp + (1 - lpCoeff) * wetRaw;
  runtime.lp = finiteOrZero(runtime.lp);
  runtime.hp = finiteOrZero(runtime.hp);

  const wet = runtime.lp;
  return finiteOrZero(dry * (1 - mix) + wet * mix);
}

function processMonoOne(
  input: number,
  node: FxNode,
  runtime: MonoFxRuntime,
  sampleRate: number,
): number {
  switch (runtime.type) {
    case "distortion":
      return processDistortion(input, node);
    case "bitcrush":
      return processBitcrush(input, node, runtime);
    case "delay":
      return processDelay(input, node, runtime, sampleRate);
    case "chorus":
      return processChorus(input, node, runtime, sampleRate);
    case "reverb":
      return processReverb(input, node, runtime);
    case "compressor":
      return processCompressor(input, node, runtime, sampleRate);
    case "eq":
      return processEq(input, node, runtime, sampleRate);
    case "filter":
      return processFilter(input, node, runtime, sampleRate);
    case "limiter":
      return processLimiter(input, node, runtime, sampleRate);
    case "flanger":
      return processFlanger(input, node, runtime, sampleRate);
    case "phaser":
      return processPhaser(input, node, runtime, sampleRate);
    case "saturation":
      return processSaturation(input, node, runtime, sampleRate);
  }
}

function processStereoOne(
  inputL: number,
  inputR: number,
  node: FxNode,
  runtime: FxRuntimeNode,
  sampleRate: number,
): StereoSample {
  switch (runtime.type) {
    case "dualMono":
      return {
        l: processMonoOne(inputL, node, runtime.left, sampleRate),
        r: processMonoOne(inputR, node, runtime.right, sampleRate),
      };
    case "colorReverb":
      return processColorReverbStereo(inputL, inputR, node, runtime);
    case "utility":
      return processUtility(inputL, inputR, node);
  }
}

/**
 * Run a stereo FX stack sample-by-sample.
 * Disabled nodes keep processing (so delay/reverb memory stays warm) but their
 * wet is smoothed toward 0 — avoids clicks on enable/wet scrub.
 */
export function processStereoFxChain(
  inputL: number,
  inputR: number,
  state: FxChainState,
  sampleRate: number,
): StereoSample {
  let l = finiteOrZero(inputL);
  let r = finiteOrZero(inputR);
  const sr = Math.max(1, sampleRate);
  const dt = 1 / sr;
  if (state.wetSmooth.length !== state.nodes.length) {
    state.wetSmooth = state.nodes.map((node, i) =>
      state.wetSmooth[i]
      ?? (node.enabled ? clamp(node.wet, 0, 1) : 0),
    );
  }
  for (let i = 0; i < state.nodes.length; i++) {
    const node = state.nodes[i]!;
    const runtime = state.runtime[i];
    if (!runtime) {
      continue;
    }
    const targetWet = node.enabled ? clamp(node.wet, 0, 1) : 0;
    const wet = advanceFxWetSmooth(state.wetSmooth[i] ?? targetWet, targetWet, dt);
    state.wetSmooth[i] = wet;
    // Always run DSP so stateful FX (delay/reverb/…) keep updating buffers
    // while wet fades — otherwise bypass→enable dumps a discontinuous buffer.
    const processed = processStereoOne(l, r, node, runtime, sr);
    if (wet <= 1e-5) {
      continue;
    }
    l = finiteOrZero(l * (1 - wet) + finiteOrZero(processed.l) * wet);
    r = finiteOrZero(r * (1 - wet) + finiteOrZero(processed.r) * wet);
  }
  return { l, r };
}

/** ~8 ms linear ramp for FX wet / enable — longer than voice pan (4 ms). */
const FX_WET_SMOOTH_SEC = 0.008;

function advanceFxWetSmooth(current: number, target: number, dt: number): number {
  if (current === target) {
    return target;
  }
  const maxDelta = dt / FX_WET_SMOOTH_SEC;
  const delta = target - current;
  if (Math.abs(delta) <= maxDelta) {
    return target;
  }
  return current + Math.sign(delta) * maxDelta;
}

/**
 * Mono helper for tests/compat: feeds identical L/R and returns the left channel.
 */
export function processFxChain(
  input: number,
  state: FxChainState,
  sampleRate: number,
): number {
  return processStereoFxChain(input, input, state, sampleRate).l;
}

function defaultWetForType(type: FxType): number {
  if (type === "colorReverb") {
    return 0.14;
  }
  if (
    type === "compressor"
    || type === "eq"
    || type === "utility"
    || type === "filter"
    || type === "limiter"
    || type === "saturation"
  ) {
    return 1;
  }
  return 0.35;
}

export function normalizeFxNode(partial: Partial<FxNode> & Pick<FxNode, "type">): FxNode {
  const defaults = FX_DEFAULT_PARAMS[partial.type];
  return {
    id: partial.id,
    type: partial.type,
    enabled: partial.enabled ?? true,
    wet: partial.wet ?? defaultWetForType(partial.type),
    params: { ...defaults, ...partial.params },
  };
}
